// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useState } from 'preact/hooks';
import { truncate } from '../format.js';
import '../styles/address-reveal.css';

/**
 * COB-359: an inline, click-to-reveal address — a lighter cousin of
 * `DepositAddressField`. At rest it is a truncated mono string so a list stays
 * scannable; clicking it expands to the full address (mono, wrapping) with a
 * Copy affordance that reuses `DepositAddressField`'s pattern ("Copy" → "Copied",
 * `aria-live="polite"`). A second click on the revealed address (or the caller's
 * explicit collapse) returns it to the truncated rest state. Display-only — this
 * is not a signing-review surface (no WYSIWYS claim).
 */
export function AddressReveal({
  address,
  /** Truncation length of the rest state — mirrors the pending-row's prior
   *  `truncate(address, 20)` so the collapsed look is byte-for-byte unchanged. */
  truncateAt = 20,
}: {
  address: string;
  truncateAt?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (!expanded) {
    return (
      <span class="address-reveal">
        <button
          type="button"
          class="address-reveal-toggle mono"
          aria-expanded={false}
          title="Show full address"
          onClick={() => setExpanded(true)}
        >
          {truncate(address, truncateAt)}
        </button>
      </span>
    );
  }

  return (
    <span class="address-reveal address-reveal--expanded">
      <button
        type="button"
        class="address-reveal-toggle address-reveal-toggle--full mono"
        aria-expanded={true}
        title="Hide full address"
        onClick={() => setExpanded(false)}
      >
        {address}
      </button>
      <button
        type="button"
        class="btn btn-link address-reveal-copy"
        onClick={handleCopy}
        aria-live="polite"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  );
}
